import React, { useState } from "react";
import { Upload, FileSpreadsheet, Factory, DollarSign, CheckCircle2, AlertCircle, Loader2, Image } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/axios";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Import Costos Excel Modal
 * Modal for importing costos.xlsx format Excel files to Factory and Quote Types
 * Supports extraction of in-cell images (Excel 365+ "Place in Cell" feature)
 */
export const ImportCostosModal = ({
  isOpen,
  onClose,
  onImportComplete,
  factoryPassword  // SEC-002 FIX: Receive password for authenticated import
}) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importToFactory, setImportToFactory] = useState(true);
  const [importToQuoteTypes, setImportToQuoteTypes] = useState(true);
  const [incrementPercentage, setIncrementPercentage] = useState(10);
  const [importResult, setImportResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        toast.error('Por favor seleccione un archivo Excel (.xlsx o .xls)');
        return;
      }
      setFile(selectedFile);
      setImportResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      if (!droppedFile.name.endsWith('.xlsx') && !droppedFile.name.endsWith('.xls')) {
        toast.error('Por favor suelte un archivo Excel (.xlsx o .xls)');
        return;
      }
      setFile(droppedFile);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Seleccione un archivo primero');
      return;
    }

    if (!importToFactory && !importToQuoteTypes) {
      toast.error('Seleccione al menos una sección para importar');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('password', factoryPassword);  // SEC-002 FIX: Include password
      formData.append('import_to_factory', importToFactory.toString());
      formData.append('import_to_quote_types', importToQuoteTypes.toString());
      formData.append('increment_percentage', incrementPercentage.toString());

      const response = await api.post(`${API}/api/factory/import-costos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResult(response.data);
      
      if (response.data.success) {
        toast.success('Importación completada exitosamente');
        if (onImportComplete) {
          onImportComplete(response.data);
        }
      }
    } catch (error) {
      console.error('Import error:', error);
      const errorMsg = error.response?.data?.detail || 'Error al importar el archivo';
      toast.error(errorMsg);
      setImportResult({ success: false, error: errorMsg });
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setImportResult(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-green-600" />
            Importar Excel de Costos
          </DialogTitle>
          <DialogDescription>
            Importe datos desde un archivo Excel en formato costos.xlsx a las secciones de Fábrica y/o Lista de Precio Venta
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* File Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-slate-400'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
          >
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileSpreadsheet className="w-8 h-8 text-green-600" />
                <div className="text-left">
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-slate-500">
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setFile(null); setImportResult(null); }}
                >
                  Cambiar
                </Button>
              </div>
            ) : (
              <>
                <Upload className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-600 mb-2">
                  Arrastre un archivo Excel aquí o haga clic para seleccionar
                </p>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                  id="costos-file-input"
                />
                <Label
                  htmlFor="costos-file-input"
                  className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Upload className="w-4 h-4" />
                  Seleccionar Archivo
                </Label>
              </>
            )}
          </div>

          {/* Import Options */}
          <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
            <h4 className="font-medium text-sm">Opciones de Importación</h4>
            
            <div className="flex items-center space-x-3">
              <Checkbox
                id="import-factory"
                checked={importToFactory}
                onCheckedChange={setImportToFactory}
              />
              <Label htmlFor="import-factory" className="flex items-center gap-2 cursor-pointer">
                <Factory className="w-4 h-4 text-orange-600" />
                Importar a Fábrica
                <span className="text-xs text-slate-500">(Columna M: +10%)</span>
              </Label>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="import-quote-types"
                checked={importToQuoteTypes}
                onCheckedChange={setImportToQuoteTypes}
              />
              <Label htmlFor="import-quote-types" className="flex items-center gap-2 cursor-pointer">
                <DollarSign className="w-4 h-4 text-green-600" />
                Importar a Lista de Precio Venta
                <span className="text-xs text-slate-500">(Columna R: Precio Venta)</span>
              </Label>
            </div>

            {importToFactory && (
              <div className="flex items-center gap-3 mt-2 pl-6">
                <Label className="text-sm">Incremento para Fábrica:</Label>
                <Input
                  type="number"
                  value={incrementPercentage}
                  onChange={(e) => setIncrementPercentage(parseFloat(e.target.value) || 0)}
                  className="w-20 h-8"
                  step="0.1"
                />
                <span className="text-sm text-slate-500">%</span>
              </div>
            )}
          </div>

          {/* Expected Format Info */}
          <div className="text-xs text-slate-500 p-3 bg-slate-100 rounded">
            <p className="font-medium mb-1">Formato esperado del Excel:</p>
            <p>A: Obra, B: Tipo Perfil, C: Código, D-E: Ancho/Alto MM, F-G: Ancho/Alto M</p>
            <p>H: Color, I: Vidrio, J: PU, K: M², L: PU/M², M: +10%, N: Imagen</p>
            <p>O: Descripción, P: Empresa, R: Precio Venta, S: PV x M²</p>
            <p className="mt-1 text-blue-600">
              ✨ Soporta imágenes "dentro de celda" (Excel 365+ Place in Cell)
            </p>
          </div>

          {/* Import Result */}
          {importResult && (
            <div className={`p-4 rounded-lg ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-start gap-3">
                {importResult.success ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`font-medium ${importResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {importResult.success ? 'Importación Exitosa' : 'Error en la Importación'}
                  </p>
                  {importResult.success && importResult.stats && (
                    <div className="text-sm text-green-700 mt-1 space-y-0.5">
                      <p>• {importResult.stats.total_rows_processed} filas procesadas</p>
                      {importResult.stats.factory_imported > 0 && (
                        <p>• {importResult.stats.factory_imported} items importados a Fábrica</p>
                      )}
                      {importResult.stats.factory_with_images > 0 && (
                        <p className="flex items-center gap-1">
                          <Image className="w-3 h-3" />
                          {importResult.stats.factory_with_images} con imágenes
                        </p>
                      )}
                      {importResult.stats.quote_types_new > 0 && (
                        <p>• {importResult.stats.quote_types_new} nuevos tipos en Lista de Precio</p>
                      )}
                      {importResult.stats.quote_types_updated > 0 && (
                        <p>• {importResult.stats.quote_types_updated} tipos actualizados</p>
                      )}
                      {importResult.stats.quote_types_with_images > 0 && (
                        <p className="flex items-center gap-1">
                          <Image className="w-3 h-3" />
                          {importResult.stats.quote_types_with_images} tipos con imágenes
                        </p>
                      )}
                      {importResult.stats.images_found > 0 && (
                        <p className="text-blue-600 flex items-center gap-1">
                          <Image className="w-3 h-3" />
                          {importResult.stats.images_found} imágenes extraídas de {importResult.stats.total_images_in_file || '?'} en archivo
                        </p>
                      )}
                      {importResult.stats.total_images_in_file > 0 && importResult.stats.images_found === 0 && (
                        <p className="text-amber-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {importResult.stats.total_images_in_file} imágenes detectadas pero no mapeadas a celdas
                        </p>
                      )}
                      {importResult.stats.skipped_rows > 0 && (
                        <p className="text-slate-500">• {importResult.stats.skipped_rows} filas vacías omitidas</p>
                      )}
                    </div>
                  )}
                  {importResult.error && (
                    <p className="text-sm text-red-700 mt-1">{importResult.error}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={handleClose}>
              {importResult?.success ? 'Cerrar' : 'Cancelar'}
            </Button>
            {!importResult?.success && (
              <Button
                onClick={handleImport}
                disabled={!file || importing}
                className="bg-green-600 hover:bg-green-700"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importando...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Importar
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportCostosModal;
